import React from 'react';

type GameInfo = {
  id: number;
  path: string;
  name: string;
  defaultGameMode?: string;
  gameModes?: { id: string; name: string }[];
};

type AutomaticGame = {
  info: GameInfo;
  localStorageKey: string;
  getStats: () => any;
  saveStats: (stats: any) => void;
  getLastGameMode: () => string | null;
  saveLastGameMode: (mode: string) => void;
  component: React.LazyExoticComponent<() => JSX.Element>;
};

const gameInfoFiles = import.meta.glob('./*/gameInfo.ts', { eager: true });

function createStatsHandlers(gameKey: string) {
  return {
    getStats: () => {
      const saved = localStorage.getItem(gameKey);
      return saved
        ? JSON.parse(saved)
        : { gamesPlayed: 0, maxRound: 0, customStat: 0 };
    },
    saveStats: (stats: any) =>
      localStorage.setItem(gameKey, JSON.stringify(stats)),
  };
}

function createGameModeHandlers(gameKey: string) {
  const lastGameModeKey = `${gameKey}LastMode`;
  return {
    getLastGameMode: () => localStorage.getItem(lastGameModeKey),
    saveLastGameMode: (mode: string) =>
      localStorage.setItem(lastGameModeKey, mode),
  };
}

const games: AutomaticGame[] = Object.entries(gameInfoFiles).map(
  ([filePath, module]) => {
    const folderName = filePath.split('/')[1];
    const { GAME_INFO } = module as { GAME_INFO: GameInfo };
    const gamePath = GAME_INFO.path.startsWith('/') 
      ? GAME_INFO.path.substring(1) 
      : GAME_INFO.path;
    const Component = React.lazy(() => import(`./${folderName}/${gamePath}.tsx`));
    const localStorageKey = folderName + 'Stats';
    const { getStats, saveStats } = createStatsHandlers(localStorageKey);
    const { getLastGameMode, saveLastGameMode } =
      createGameModeHandlers(folderName);
    return {
      info: GAME_INFO,
      localStorageKey,
      getStats,
      saveStats,
      getLastGameMode,
      saveLastGameMode,
      component: Component,
    };
  }
);

const sortedGames = games.sort((a, b) => a.info.id - b.info.id);

export const getGames = (): AutomaticGame[] => sortedGames;
export const getGame = (id: number): AutomaticGame | undefined =>
  sortedGames.find((game) => game.info.id === id);
export const getGameByPath = (path: string): AutomaticGame | undefined =>
  sortedGames.find((game) => game.info.path === path);
export const getNextGame = (currentId: number): AutomaticGame | undefined => {
  const idx = sortedGames.findIndex((game) => game.info.id === currentId);
  return idx === -1 ? undefined : sortedGames[(idx + 1) % sortedGames.length];
};
export const getPreviousGame = (
  currentId: number
): AutomaticGame | undefined => {
  const idx = sortedGames.findIndex((game) => game.info.id === currentId);
  return idx === -1
    ? undefined
    : sortedGames[(idx - 1 + sortedGames.length) % sortedGames.length];
};
